import { BUDGET, DECISION, HUMAN_RESOURCES } from 'business/constants';
import { Box, styled } from '@mui/material';
import { EnrichedUserT, ScopeT } from 'business/definitions';
import React, { useCallback } from 'react';

import AccessTypeCellRenderer from './components/AccessTypeCellRenderer';
import ActionsCellRenderer from './components/ActionsCellRenderer';
import AgContainer from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/components/AgContainer';
import { AgGridReact } from 'ag-grid-react';
import BooleanCellRenderer from 'ui/AgGridReact/CellRenderers/BooleanCellRenderer';
import { GetRowIdParams } from 'ag-grid-community';
import Loading from 'components/Loading';
import TooltipCellRenderer from 'routes/BudgetModule/components/BudgetOverview/BudgetSynthesisAndRatios/BudgetBalance/TooltipCellRenderer';
import { getColumns } from './helpers/colDefinitions';
import { getDefaultExportParams } from './helpers/gridHelper';
import { useClientHasModule } from 'hooks/client';
import { useIsSupportUser } from 'hooks/user';
import { useTranslation } from 'react-i18next';

const StyAgContainer = styled(AgContainer, { name: 'StyAgContainer' })(
    ({ theme }) => ({
        border: '1px solid lightgray',
        margin: '0.5rem',
        width: 'auto',
        flex: 15,
        '.ag-header-cell': {
            padding: '0 !important',
            textAlign: 'center',
        },
        '.ag-header-cell-label': {
            justifyContent: 'center',
        },
        '.ag-header-group-cell > .ag-header-cell-comp-wrapper': {
            justifyContent: 'center',
        },
        '.ag-header-cell-text': {
            textTransform: 'unset !important',
            color: `${theme.palette.darkGrey} !important`,
        },
        '.ag-header-group-text': {
            textTransform: 'unset !important',
            overflow: 'visible !important',
            whiteSpace: 'normal !important',
            textAlign: 'center',
            color: `${theme.palette.primary.main} !important`,
        },
        '.ag-columns-group': {
            borderRight: '1px solid lightgray',
        },
    }),
);

type Props = {
    users: EnrichedUserT[];
    clientScopes: ScopeT[];
    isLoading: boolean;
};

const UsersGrid = ({ users, clientScopes, isLoading }: Props): JSX.Element => {
    const { t } = useTranslation();
    const hasBudget = useClientHasModule(BUDGET);
    const hasDecision = useClientHasModule(DECISION);
    const hasHumanResources = useClientHasModule(HUMAN_RESOURCES);
    const isSupportUser = useIsSupportUser();
    const canUserSeeHumanResourcesSettings = hasHumanResources && isSupportUser;

    const columns = getColumns({
        hasDecision,
        hasBudget,
        canUserSeeHumanResourcesSettings,
        clientScopes,
        users,
        t,
    });
    const defaultExportParams = getDefaultExportParams(t);

    const getRowId = useCallback(
        (params: GetRowIdParams) => params.data.email,
        [],
    );
    return (
        <>
            {isLoading ? (
                <Box sx={{ flex: 15 }}>
                    <Loading />
                </Box>
            ) : (
                <StyAgContainer className="ag-theme-material">
                    <AgGridReact
                        rowHeight={40}
                        floatingFiltersHeight={56}
                        headerHeight={50}
                        getRowId={getRowId}
                        rowData={users}
                        overlayNoRowsTemplate={t('adminSpace.usersList.noUser')}
                        components={{
                            booleanCellRenderer: BooleanCellRenderer,
                            accessTypeCellRenderer: AccessTypeCellRenderer,
                            actionsCellRenderer: ActionsCellRenderer,
                            tooltipCellRenderer: TooltipCellRenderer,
                        }}
                        columnDefs={columns}
                        defaultColDef={{
                            columnGroupShow: 'open',
                            menuTabs: [],
                            flex: 2,
                            resizable: false,
                            enableRowGroup: false,
                            sortable: true,
                            suppressNavigable: true,
                        }}
                        gridOptions={{
                            defaultExcelExportParams: defaultExportParams,
                            // @ts-expect-error types
                            defaultCsvExportParams: defaultExportParams,
                        }}
                        animateRows={false}
                        columnMenu="legacy"
                    />
                </StyAgContainer>
            )}
        </>
    );
};

export default UsersGrid;
